// @ts-strict-ignore
import { useAppDispatch, useAppSelector } from "src/common/state/hooks";
import ModalStandard from "../../../common/components/Modal/ModalStandard";
import { closeStandardModal } from "../../../common/state/slice/modal/modalSlice";
import type { IMedia } from "../../../model/unityObject";
import { Gallery } from "../../gallery/Gallery";
import Alert from "../../../common/components/Alert/Alert";
import { getCurrentProject } from "src/features/projects/state/projectsSlice";
import { useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import { getMediaSearch } from "src/features/gallery/state/gallerySlice";
import { setAlert } from "src/features/alert/state/alertsSlice";
import { useTranslation } from "react-i18next";

// Todo: have 'nodeId' for: ["profile", "organization", "background_audio"]

const ModalGallery = ({
  onMediaChange,
  onCancel,
  nodeId,
  contentType,
  allowedMediaTypeOverride,
  allowedMedia360TagOverride,
  uploadAcceptOverride,
  onUploadedFileSelect,
}: {
  nodeId: string;
  onMediaChange: (id: IMedia) => void;
  onCancel?: () => void;
  contentType?: string;
  allowedMediaTypeOverride?: string;
  allowedMedia360TagOverride?: string;
  uploadAcceptOverride?: string;
  // Divert the gallery's upload entries (button + drag-and-drop) to the caller
  // instead of the upload slide-over; see Gallery's prop of the same name.
  onUploadedFileSelect?: (file: File) => void;
}) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const onCancelDefault = () => {
    dispatch(closeStandardModal("modal-gallery"));
  };

  const { pathname } = useLocation();
  const currentProject = useAppSelector(getCurrentProject);
  const currentMediaSearch = useAppSelector(getMediaSearch);
  const [navTitle, setNavTitle] = useState("");

  // WIXAR-3811 | Incomplete path displayed in ModalGallery when editing a project
  useEffect(() => {
    if (/^\/courses\/\d{1,256}\/list\/\d{1,256}\/graph\/node\/[^/]+\/\d{1,256}$/.test(pathname)) {
      setNavTitle(currentProject?.name);
    }
  }, [pathname, currentProject]);

  return (
    <ModalStandard
      id="modal-gallery"
      modalType="standard"
      clickOnBackgroundClose={false}
      fullScreen
      isStatic
    >
      <div className="flex w-full">
        <div className="text-center ">
          <Alert />
        </div>
        <div className="grow overflow-y-visible ">
          <Gallery
            onConfirmSelectMedia={(media: IMedia) => {
              // Validate against the field's ACCEPTED types, NOT the user's current
              // Format filter (mediaSearch.mediaType). The filter is a single,
              // switchable value used only to display/narrow the grid; the field's
              // accepted set (allowedMediaTypeOverride, e.g. "image|video" for a
              // scene) is what selection must be checked against. Using the filter
              // here would wrongly block picking an image while the filter showed
              // "video" (Story 13.20: AC2).
              const expectedMediaType = allowedMediaTypeOverride;

              // Check if there's a specific media type requirement (not "all" or empty)
              const hasSpecificTypeRequirement =
                !isStringNullOrEmpty(expectedMediaType) && expectedMediaType !== "all";

              // The requirement may be a "|"-separated set (e.g. "image|video"
              // for scenes), mirroring allowedMediaTypeOverride and the backend
              // source-media filter. Validate membership, not exact equality.
              const allowedMediaTypes = (expectedMediaType ?? "")
                .split("|")
                .map((type) => type.trim())
                .filter(Boolean);

              // If specific type required, validate the selection is in the set
              if (hasSpecificTypeRequirement && !allowedMediaTypes.includes(media.type ?? "")) {
                dispatch(
                  setAlert({
                    isOpen: true,
                    type: "danger",
                    msg: t("error.invalidMediaType", {
                      expected: expectedMediaType,
                      actual: media.type,
                    }),
                  }),
                );
                return; // Block invalid selection
              }

              // Validate 360 tag if specified
              const expected360Tag = currentMediaSearch?.media360tag;
              const hasSpecific360TagRequirement =
                !isStringNullOrEmpty(expected360Tag) && expected360Tag !== "all";

              if (hasSpecific360TagRequirement && media.media_360_tag !== expected360Tag) {
                dispatch(
                  setAlert({
                    isOpen: true,
                    type: "danger",
                    msg: t("error.invalid360Tag", {
                      expected: expected360Tag,
                      actual: media.media_360_tag,
                    }),
                  }),
                );
                return; // Block invalid selection
              }

              // All validations passed
              onMediaChange(media);
            }}
            onCancel={() => (onCancel ? onCancel() : onCancelDefault())}
            nodeId={nodeId}
            contentType={contentType}
            title={navTitle}
            allowedMediaTypeOverride={allowedMediaTypeOverride}
            isModalEdit={false}
            isMediaPickerMode
            isModalGallery
            allowedMedia360TagOverride={allowedMedia360TagOverride}
            uploadAcceptOverride={uploadAcceptOverride}
            onUploadedFileSelect={onUploadedFileSelect}
          />
        </div>
      </div>
    </ModalStandard>
  );
};

export default ModalGallery;
